/**
 * @file AddSecurityRole.js
 * @description Wizard container for adding or editing a security role. Manages a 3-step flow: Define → Multi Tenant Attributes → Client Attributes.
 */
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    fetchSecurityAttributes,
    fetchSecurityAttributeGroups,
    fetchSecurityRoleById,
    updateSecurityRole,
    createSecurityRole,
} from '../../services/securityRoleService';
import Toast from '../ui/toasts/Toast';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import styles from './security-roles.module.scss';
import Spinner from '../ui/spinners/Spinner';
import { useAuthContext } from '../../store/Contexts';
import {
    hasReadOnlyPermission,
    hasAddEditPermission,
} from './SecurityRolePermission';
import { ROUTES } from '../../constants/routes';
import SecurityRoleWizardStepper from './SecurityRoleWizardStepper';
import SecurityRoleDefineTab from './tabs/SecurityRoleDefineTab';
import SecurityRoleMTAttributesTab from './tabs/SecurityRoleMTAttributesTab';
import SecurityRoleClientAttributesTab from './tabs/SecurityRoleClientAttributesTab';
import { getText } from '../../i18n';

const STEP_DEFINE = 'Define';
const STEP_MT = 'Multi Tenant Attribute';
const STEP_CLIENT = 'Client Attribute';

const LEVEL_NO_ACCESS = 0;
const LEVEL_FULL_ACCESS = 2;

const buildAttributesByGroup = attributes =>
    attributes.reduce((acc, attr) => {
        const groupId = attr.securityAttributeGroupId;
        if (!acc[groupId]) acc[groupId] = [];
        acc[groupId].push(attr);
        return acc;
    }, {});

const buildGroupsByCategory = groups =>
    groups.reduce((acc, group) => {
        const catId = group.securityAttributeGroupCategoryId;
        if (!acc[catId]) acc[catId] = [];
        acc[catId].push(group);
        return acc;
    }, {});

const buildUniqueCategories = groups => {
    const seen = new Set();
    const categories = groups.reduce((acc, group) => {
        if (!seen.has(group.securityAttributeGroupCategoryId)) {
            seen.add(group.securityAttributeGroupCategoryId);
            acc.push(group.category);
        }
        return acc;
    }, []);
    return categories.sort((a, b) => a.name.localeCompare(b.name));
};

const deriveGroupSelectionsFromIds = (selectedIds, attributesByGroup) => {
    const selections = {};
    Object.entries(attributesByGroup).forEach(([groupId, attrs]) => {
        const matched = attrs.find(attr => selectedIds.includes(attr.id));
        selections[Number(groupId)] = matched
            ? matched.securityAttributeLevelId
            : LEVEL_NO_ACCESS;
    });
    return selections;
};

const deriveAttributeIdsFromSelections = (groupSelections, attributesByGroup) =>
    Object.entries(groupSelections).flatMap(([groupId, level]) => {
        if (level === LEVEL_NO_ACCESS) return [];
        const attrs = attributesByGroup[groupId] ?? [];
        const match = attrs.find(
            attr => attr.securityAttributeLevelId === level,
        );
        return match ? [match.id] : [];
    });

const sortGroupsByDependency = (groups, attributesByGroup, allAttributes) => {
    const groupIdSet = new Set(groups.map(g => g.id));
    const groupParentIds = new Map(
        groups.map(group => {
            const attrs = attributesByGroup[group.id] ?? [];
            const parentIds = [
                ...new Set(
                    attrs
                        .flatMap(
                            attr => attr.dependsOnSecurityAttributeIds ?? [],
                        )
                        .map(
                            depAttrId =>
                                allAttributes.find(a => a.id === depAttrId)
                                    ?.securityAttributeGroupId,
                        )
                        .filter(id => id != null && groupIdSet.has(id)),
                ),
            ];
            return [group.id, parentIds];
        }),
    );

    const sorted = [];
    const visited = new Set();

    const visit = group => {
        if (visited.has(group.id)) return;
        visited.add(group.id);
        groupParentIds.get(group.id).forEach(parentId => {
            const parentGroup = groups.find(g => g.id === parentId);
            if (parentGroup) visit(parentGroup);
        });
        sorted.push(group);
    };

    groups.forEach(visit);
    return sorted;
};

const applyDependencySortToMap = (
    groupsByCategory,
    attributesByGroup,
    allAttributes,
) =>
    Object.fromEntries(
        Object.entries(groupsByCategory).map(([catId, groups]) => [
            catId,
            sortGroupsByDependency(groups, attributesByGroup, allAttributes),
        ]),
    );

const computeDisabledGroups = (
    groups,
    attributesByGroup,
    allAttributes,
    groupSelections,
) => {
    const disabled = [];
    groups.forEach(group => {
        const groupAttrs = attributesByGroup[group.id] ?? [];
        const depIds = groupAttrs.flatMap(
            attr => attr.dependsOnSecurityAttributeIds ?? [],
        );
        if (!depIds.length) return;
        const parentSatisfied = depIds.some(depAttrId => {
            const parentAttr = allAttributes.find(a => a.id === depAttrId);
            if (!parentAttr) return false;
            return (
                groupSelections[parentAttr.securityAttributeGroupId] >=
                LEVEL_FULL_ACCESS
            );
        });
        if (!parentSatisfied) disabled.push(group.id);
    });
    return disabled;
};

const AddSecurityRole = () => {
    const { showModal } = useContext(ModalContext);
    const navigate = useNavigate();
    const location = useLocation();
    const { roleId } = useParams();
    const { multitenantFlag, clientTierMeetsRequirement, hasSecurityAttribute } = useAuthContext();

    const isNewRole = location.pathname.includes('add');
    const isReadOnly = hasReadOnlyPermission(multitenantFlag, hasSecurityAttribute);
    const canEdit = hasAddEditPermission(multitenantFlag, hasSecurityAttribute);

    const steps = multitenantFlag
        ? [STEP_DEFINE, STEP_MT, STEP_CLIENT]
        : [STEP_DEFINE, STEP_CLIENT];

    const [activeStepIndex, setActiveStepIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isFormDirty, setIsFormDirty] = useState(false);

    const [roleDetails, setRoleDetails] = useState({
        name: '',
        description: '',
        tenantId: window.localStorage.getItem('tenantId'),
        isActive: true,
    });

    const [groupSelections, setGroupSelections] = useState({});
    const [allAttributes, setAllAttributes] = useState([]);
    const [attributesByGroup, setAttributesByGroup] = useState({});
    const [mtCategories, setMtCategories] = useState([]);
    const [mtGroupsByCategory, setMtGroupsByCategory] = useState({});
    const [clientCategories, setClientCategories] = useState([]);
    const [clientGroupsByCategory, setClientGroupsByCategory] = useState({});

    useEffect(() => {
        const loadData = async () => {
            try {
                let [allAttributes, allGroups] = await Promise.all([
                    fetchSecurityAttributes(true, true),
                    fetchSecurityAttributeGroups(true, true),
                ]);

                if (!multitenantFlag)
                    allAttributes = allAttributes.filter(attr =>
                        attr.minimumRequiredTenantTierId === null ||
                        clientTierMeetsRequirement(attr.minimumRequiredTenantTierId)
                    );

                const byGroup = buildAttributesByGroup(allAttributes);
                setAllAttributes(allAttributes);
                setAttributesByGroup(byGroup);

                const clientGroupIds = new Set(
                    allAttributes
                        .filter(attr => attr.isClientAdmin)
                        .map(attr => attr.securityAttributeGroupId),
                );
                const mtGroups = allGroups.filter(
                    g => !clientGroupIds.has(g.id),
                );
                const clientGroups = allGroups.filter(g =>
                    clientGroupIds.has(g.id),
                );

                setMtCategories(buildUniqueCategories(mtGroups));
                setMtGroupsByCategory(
                    applyDependencySortToMap(
                        buildGroupsByCategory(mtGroups),
                        byGroup,
                        allAttributes,
                    ),
                );
                setClientCategories(buildUniqueCategories(clientGroups));
                setClientGroupsByCategory(
                    applyDependencySortToMap(
                        buildGroupsByCategory(clientGroups),
                        byGroup,
                        allAttributes,
                    ),
                );

                if (!isNewRole && roleId) {
                    const fetchedRole = await fetchSecurityRoleById(
                        parseInt(roleId, 10),
                    );
                    setRoleDetails({
                        name: fetchedRole.name,
                        description: fetchedRole.description ?? '',
                        tenantId: fetchedRole.tenantId,
                        isActive: fetchedRole.isActive,
                    });
                    setGroupSelections(
                        deriveGroupSelectionsFromIds(
                            fetchedRole.securityAttributes ?? [],
                            byGroup,
                        ),
                    );
                }
            } catch (error) {
                console.error('Failed to load security role data', error);
                Toast.error(getText('msg_3bcb8c740c1f'));
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [roleId, isNewRole, multitenantFlag]);

    const handleDetailsChange = updated => {
        setRoleDetails(prev => ({ ...prev, ...updated }));
        setIsFormDirty(true);
    };

    const handleLevelChange = (groupId, level) => {
        setGroupSelections(prev => {
            const next = { ...prev, [groupId]: level };

            if (level < LEVEL_FULL_ACCESS) {
                const allGroups = [
                    ...Object.values(mtGroupsByCategory).flat(),
                    ...Object.values(clientGroupsByCategory).flat(),
                ];
                computeDisabledGroups(
                    allGroups,
                    attributesByGroup,
                    allAttributes,
                    next,
                ).forEach(disabledGroupId => {
                    if (next[disabledGroupId] !== LEVEL_NO_ACCESS) {
                        next[disabledGroupId] = LEVEL_NO_ACCESS;
                    }
                });
            }

            return next;
        });
        setIsFormDirty(true);
    };

    const returnToList = () => navigate(ROUTES.SECURITY_ROLES);

    const handleClose = () => {
        if (isReadOnly) return returnToList();
        if (!isFormDirty) return returnToList();

        showModal({
            type: ModalTypes.ACTION,
            title: getText('msg_b78130c92f32'),
            subtitle: getText('msg_2f4e8fc9ac09'),
            text: getText('msg_f3cefc556397'),
            onYes: returnToList,
            onNo: () => { },
            noButtonText: getText('msg_816c52fd2bdd'),
            yesButtonText: getText('msg_5397e0583f14'),
        });
    };

    const handleNext = () => {
        if (!roleDetails.name?.trim()) {
            Toast.error(getText('msg_90b33741dc75'));
            return;
        }
        setActiveStepIndex(prev => Math.min(prev + 1, steps.length - 1));
    };

    const handleStepClick = index => {
        if (index === 0 || roleDetails.name?.trim()) {
            setActiveStepIndex(index);
            return;
        }
        Toast.error(getText('msg_aae49272320b'));
    };

    const handleSave = async () => {
        if (!roleDetails.name?.trim()) {
            Toast.error(getText('msg_90b33741dc75'));
            setActiveStepIndex(0);
            return;
        }

        if (!isFormDirty) {
            Toast.warning(getText('msg_13c3a7ac4e47'));
            return;
        }

        setIsSaving(true);
        try {
            const securityAttributes = deriveAttributeIdsFromSelections(
                groupSelections,
                attributesByGroup,
            );

            const roleToSave = {
                id: isNewRole ? 0 : roleId,
                tenantId: multitenantFlag ? 0 : roleDetails.tenantId,
                name: roleDetails.name.trim(),
                description: roleDetails.description,
                isActive: isNewRole ? true : roleDetails.isActive,
                securityAttributes,
            };

            if (isNewRole) {
                await createSecurityRole(roleToSave);
            } else {
                await updateSecurityRole(roleToSave);
            }

            Toast.success(getText('msg_f1226db433f8'));
            if (isNewRole) navigate(ROUTES.SECURITY_ROLES);
            else setIsFormDirty(false);
        } catch (error) {
            const httpStatusCode = error?.response?.data?.status;
            const action = isNewRole ? 'add' : 'update';

            if (httpStatusCode === 400) {
                const message = error?.response?.data?.detail;
                Toast.error(message);
            } else {
                Toast.error(`Error ${action} the role.`);
            }

            console.error(`Failed to ${action} role:`, error);
        } finally {
            setIsSaving(false);
        }
    };

    const isFirstStep = activeStepIndex === 0;
    const isLastStep = activeStepIndex === steps.length - 1;
    const activeStep = steps[activeStepIndex];

    const allGroups = [
        ...Object.values(mtGroupsByCategory).flat(),
        ...Object.values(clientGroupsByCategory).flat(),
    ];
    const disabledGroups = computeDisabledGroups(
        allGroups,
        attributesByGroup,
        allAttributes,
        groupSelections,
    );

    const dependentGroupIds = new Set(
        allAttributes
            .filter(
                attr => (attr.dependsOnSecurityAttributeIds ?? []).length > 0,
            )
            .map(attr => attr.securityAttributeGroupId),
    );

    return (
        <div className={`page-content ${styles.container}`}>
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div className="d-flex justify-content-between align-items-center pb-3 px-4">
                        <p className="page-title card-title mb-0">
                            {isNewRole
                                ? 'Add New Security Role'
                                : `${roleDetails.name}`}
                        </p>
                        <div className="d-flex gap-3">
                            {canEdit && (
                                <span
                                    type="button"
                                    onClick={handleSave}
                                    className="ing-text-buttons d-flex align-items-center"
                                >
                                    <i className="bi bi-check me-2" />
                                    {isSaving ? 'Saving...' : 'Save'}
                                </span>
                            )}
                            <span
                                type="button"
                                onClick={handleClose}
                                className="ing-text-buttons d-flex align-items-center"
                            >
                                <i className="bi bi-x me-2" />
                                {getText('msg_bbfa773e5a63')}
                            </span>
                        </div>
                    </div>

                    <SecurityRoleWizardStepper
                        steps={steps}
                        activeStepIndex={activeStepIndex}
                        onStepClick={handleStepClick}
                    />

                    {isLoading ? (
                        <Spinner width={50} className={styles.loadingSpinner} />
                    ) : (
                        <>
                            {activeStep === STEP_DEFINE && (
                                <SecurityRoleDefineTab
                                    data={roleDetails}
                                    onChange={handleDetailsChange}
                                    readOnly={isReadOnly}
                                />
                            )}

                            {activeStep === STEP_MT && (
                                <SecurityRoleMTAttributesTab
                                    categories={mtCategories}
                                    groupsByCategory={mtGroupsByCategory}
                                    attributesByGroup={attributesByGroup}
                                    groupSelections={groupSelections}
                                    onLevelChange={handleLevelChange}
                                    disabledGroups={disabledGroups}
                                    dependentGroupIds={dependentGroupIds}
                                    readOnly={isReadOnly}
                                />
                            )}

                            {activeStep === STEP_CLIENT && (
                                <SecurityRoleClientAttributesTab
                                    categories={clientCategories}
                                    groupsByCategory={clientGroupsByCategory}
                                    attributesByGroup={attributesByGroup}
                                    groupSelections={groupSelections}
                                    onLevelChange={handleLevelChange}
                                    disabledGroups={disabledGroups}
                                    dependentGroupIds={dependentGroupIds}
                                    readOnly={isReadOnly}
                                />
                            )}

                            <div
                                className={`${styles.wizardFooter} px-4 w-100`}
                            >
                                <div className={styles.footerActions}>
                                    {isFirstStep && (
                                        <span className={styles.footerNote}>
                                            <span className="text-danger">
                                                *
                                            </span>{' '}
                                            {getText('msg_7c118f042eaa')}
                                        </span>
                                    )}
                                    {!isFirstStep && (
                                        <button
                                            type="button"
                                            onClick={returnToList}
                                            className="btn ing-info-btn-outline"
                                        >
                                            {getText('msg_77dfd2135f4d')}
                                        </button>
                                    )}

                                    {isFirstStep && (
                                        <button
                                            type="button"
                                            onClick={handleNext}
                                            className="button-base ing-button-primary-left align-middle"
                                        >
                                            {getText('msg_51802ba8fa25')}
                                        </button>
                                    )}

                                    {!isFirstStep && !isLastStep && (
                                        <>
                                            {canEdit && (
                                                <button
                                                    type="button"
                                                    onClick={handleSave}
                                                    disabled={isSaving}
                                                    className="button-base ing-button-primary-left align-middle"
                                                >
                                                    {isSaving
                                                        ? 'Saving...'
                                                        : 'Save'}
                                                </button>
                                            )}
                                        </>
                                    )}

                                    {isLastStep && (
                                        <>
                                            {canEdit && (
                                                <button
                                                    type="button"
                                                    onClick={handleSave}
                                                    disabled={isSaving}
                                                    className="button-base ing-button-primary-left align-middle"
                                                >
                                                    {isSaving
                                                        ? 'Saving...'
                                                        : 'Save'}
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AddSecurityRole;
